import React, { useCallback, useEffect, useState } from "react";
import { api } from "../utils/api";
import { useI18n } from "../context/I18nContext";
import { useApp } from "../context/AppContext";
import { isPhotoImage } from "../utils/images";

const SLOTS = [
  { key: "breakfast", labelKey: "breakfast", icon: "☀️" },
  { key: "lunch", labelKey: "lunch", icon: "🌤️" },
  { key: "dinner", labelKey: "dinner", icon: "🌙" },
];

const CATEGORIES = [
  { value: "all", labelKey: "all" },
  { value: "healthy", labelKey: "healthy" },
  { value: "tasty", labelKey: "tasty" },
  { value: "balanced", labelKey: "balanced" },
];

export default function DailyPlan({ navigate }) {
  const { t, language } = useI18n();
  const { actions } = useApp();
  const [plan, setPlan] = useState(null);
  const [cuisines, setCuisines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [cuisine, setCuisine] = useState("all");
  const [limitInfo, setLimitInfo] = useState(null);

  useEffect(() => {
    api.getCuisines().then((response) => setCuisines(response.data)).catch(console.error);
  }, []);

  const fetchPlan = useCallback(async (refresh = false) => {
    setLoading(true);
    setLimitInfo(null);
    try {
      const response = await api.getDailyPlan({
        category,
        cuisine,
        refresh,
        lang: language,
        ...actions.usageProfile(),
      });
      setPlan(response.data);
    } catch (error) {
      if (error?.code === "LIMIT_REACHED") {
        setLimitInfo(error.usage || {});
      } else {
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  }, [category, cuisine, language, actions]);

  useEffect(() => {
    fetchPlan(false);
  }, [fetchPlan]);

  const meals = plan ? SLOTS.map((slot) => plan[slot.key]).filter(Boolean) : [];
  const totalCalories = meals.reduce((sum, meal) => sum + (meal.calories || 0), 0);
  const hasCompleteCalories = meals.length === SLOTS.length && meals.every((meal) => Number(meal.calories) > 0);

  const openDetail = async (meal) => {
    try {
      navigate("detail", meal);
    } catch (error) {
      window.alert(error.message || t("requestFailed"));
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>📅 {t("daily")}</h1>
        <p>{t("dailyIntro")}</p>
      </div>

      <div className="filter-bar filter-panel">
        <div className="filter-row">
          <span className="filter-label">{t("category")}</span>
          <div className="filter-options">
            {CATEGORIES.map((item) => (
              <button
                key={item.value}
                className={`filter-btn ${category === item.value ? "active" : ""}`}
                onClick={() => setCategory(item.value)}
              >
                {t(item.labelKey)}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-row">
          <span className="filter-label">{t("cuisine")}</span>
          <select value={cuisine} onChange={(event) => setCuisine(event.target.value)} className="select-input">
            <option value="all">{t("allWorldCuisines")}</option>
            {cuisines.map((item) => (
              <option key={item.name} value={item.name}>{item.name}</option>
            ))}
          </select>
          <button className="btn btn-primary btn-sm" onClick={() => fetchPlan(true)}>🔄 {t("newPlan")}</button>
        </div>

        {limitInfo && (
          <div className="reward-box">
            <strong>Bugünkü kullanım limitine ulaştın.</strong>
            <span>Kullanım hakkın gece 00.00'da yenilenir.</span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner" />
          <p>{t("loadingDaily")}</p>
        </div>
      ) : (
        <>
          {hasCompleteCalories && (
            <div className="daily-summary">
              <div>
                <span>🔥</span>
                <strong>{totalCalories.toLocaleString(language === "tr" ? "tr-TR" : undefined)} {t("kcal")}</strong>
              </div>
              <p>{t("totalDailyCalories")}</p>
              {plan?.cached && <small>{t("cachedPlan")}</small>}
            </div>
          )}

          <div className="daily-plan-grid">
            {SLOTS.map((slot) => {
              const meal = plan?.[slot.key];
              return (
                <div key={slot.key} className="meal-slot">
                  <div className="meal-slot-header">
                    <span className="slot-icon">{slot.icon}</span>
                    <h3>{t(slot.labelKey)}</h3>
                  </div>
                  {meal ? (
                    <div className="meal-slot-card" onClick={() => openDetail(meal)}>
                      <span className={`slot-emoji ${isPhotoImage(meal.image) ? "has-photo" : ""}`}>
                        {isPhotoImage(meal.image) ? <img src={meal.image} alt={meal.title} /> : meal.image}
                      </span>
                      <div className="slot-meal-title">{meal.title}</div>
                      <div className="slot-meta">
                        <span className="tag tag-cuisine">{meal.localized?.cuisine || meal.cuisine}</span>
                        <span className={`tag tag-${meal.category}`}>{meal.localized?.category || t(meal.category)}</span>
                      </div>
                      <div className="slot-stats">
                        <span>⏱ {meal.prepTime + meal.cookTime} {t("minutes")}</span>
                        <span>{Number(meal.calories) > 0 ? `🔥 ${meal.calories} ${t("kcal")}` : t("nutritionUnverified")}</span>
                        <span>🥕 {meal.ingredients.length} {t("ingredientUnit")}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="empty-state compact">
                      <p>{t("noSlotSuggestion")}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
